import { useState, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AuthShell } from './AuthShell';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, checkboxClass } from '../common/Input';
import { apiError } from '../../utils/apiError';
import { useAuthOptions } from '../../hooks/useAuthOptions';

interface LocationState {
  message?: string;
  /** Where the user was heading before being bounced to the login screen. */
  from?: string;
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { registrationEnabled } = useAuthOptions();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      navigate(state.from || '/', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell subtitle="Sign in to your account">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Previously dropped on the floor: RegisterPage navigates here with a
            success message that nothing ever read. */}
        {state.message && <Alert tone="success">{state.message}</Alert>}
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Email" required>
          {p => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          )}
        </Field>

        <Field label="Password" required>
          {p => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          )}
        </Field>

        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              className={checkboxClass}
            />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" loading={loading} className="w-full" size="lg">
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>

        {registrationEnabled && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-brand-600 dark:text-brand-400 hover:underline">Sign up</Link>
          </p>
        )}

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Have a guest passcode?{' '}
            <Link to="/guest" className="text-brand-600 dark:text-brand-400 hover:underline">Access as guest</Link>
          </p>
        </div>
      </form>
    </AuthShell>
  );
}
